import { useEffect, useRef, useCallback, useState, type RefObject } from 'react'

export interface UseAutoScrollOptions {
  /**
   * Threshold in pixels from bottom to consider "near bottom"
   * @default 100
   */
  threshold?: number
  /**
   * Whether to auto-scroll on mount
   * @default true
   */
  scrollOnMount?: boolean
  /**
   * Whether deep DOM mutations should trigger auto-scroll.
   * Keep this disabled for static/history renders to avoid extra layout work.
   * @default true
   */
  observeMutations?: boolean
  /**
   * Dependencies that trigger auto-scroll when changed
   */
  dependencies?: unknown[]
}

export interface UseAutoScrollReturn {
  /**
   * Ref to attach to the scrollable container
   */
  containerRef: RefObject<HTMLDivElement>
  /**
   * Ref to attach to the bottom sentinel element
   */
  sentinelRef: RefObject<HTMLDivElement>
  /**
   * Whether the user is currently near the bottom
   */
  isNearBottom: boolean
  /**
   * Manually scroll to bottom
   */
  scrollToBottom: () => void
  /**
   * Check if user is near bottom
   */
  checkIsNearBottom: () => boolean
}

export const useAutoScroll = (
  options: UseAutoScrollOptions = {},
  dependencies: unknown[] = []
): UseAutoScrollReturn => {
  const { threshold = 50, scrollOnMount = true, observeMutations = true } = options

  const containerRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const [isNearBottom, setIsNearBottom] = useState(true)
  const userScrolledRef = useRef(false)
  const isScrollingRef = useRef(false)
  const rafRef = useRef<number>()

  const checkIsNearBottom = useCallback((): boolean => {
    const container = containerRef.current
    if (!container) return false
    const { scrollTop, scrollHeight, clientHeight } = container
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight
    return distanceFromBottom <= threshold
  }, [threshold])

  const scrollToBottom = useCallback(() => {
    const container = containerRef.current
    const sentinel = sentinelRef.current
    if (!container || !sentinel) return
    // Use instant scroll, no smooth behavior
    sentinel.scrollIntoView({ behavior: 'auto', block: 'end' })
  }, [])

  // Update isNearBottom state
  const updateNearBottom = useCallback(() => {
    const nearBottom = checkIsNearBottom()
    setIsNearBottom(nearBottom)

    // If user scrolls back to bottom, reset the userScrolled flag
    if (nearBottom) {
      userScrolledRef.current = false
    }
  }, [checkIsNearBottom])

  // Handle user scroll events
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    let lastScrollTop = container.scrollTop
    let scrollTimeout: number

    const handleScroll = () => {
      const currentScrollTop = container.scrollTop

      // Detect if user manually scrolled (not programmatic)
      if (!isScrollingRef.current) {
        const scrolledUp = currentScrollTop < lastScrollTop
        const scrolledDown = currentScrollTop > lastScrollTop

        if (scrolledUp || (scrolledDown && !checkIsNearBottom())) {
          userScrolledRef.current = true
        }
      }

      lastScrollTop = currentScrollTop
      updateNearBottom()

      // Clear existing timeout
      if (scrollTimeout) {
        clearTimeout(scrollTimeout)
      }

      // Reset isScrolling flag after scroll ends
      scrollTimeout = window.setTimeout(() => {
        isScrollingRef.current = false
      }, 150)
    }

    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      container.removeEventListener('scroll', handleScroll)
      if (scrollTimeout) {
        clearTimeout(scrollTimeout)
      }
    }
  }, [checkIsNearBottom, updateNearBottom])

  // MutationObserver to watch for deep child mutations
  useEffect(() => {
    if (!observeMutations) return

    const container = containerRef.current
    if (!container) return

    const mutationObserver = new MutationObserver(() => {
      // Only auto-scroll if user hasn't manually scrolled away
      if (!userScrolledRef.current && isNearBottom) {
        // Use requestAnimationFrame for smooth DOM updates
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current)
        }
        rafRef.current = requestAnimationFrame(() => {
          isScrollingRef.current = true
          scrollToBottom()
          // Reset flag after a short delay
          setTimeout(() => {
            isScrollingRef.current = false
          }, 50)
        })
      }

      // Always update near bottom state
      updateNearBottom()
    })

    // Observe all mutations in the container and its deep children
    mutationObserver.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: false
    })

    return () => {
      mutationObserver.disconnect()
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
    }
  }, [isNearBottom, observeMutations, scrollToBottom, updateNearBottom])

  // Handle dependency changes (messages, content, etc.)
  useEffect(() => {
    if (dependencies.length === 0) return

    // Only auto-scroll if user is near bottom and hasn't manually scrolled
    if (!userScrolledRef.current && isNearBottom) {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
      rafRef.current = requestAnimationFrame(() => {
        isScrollingRef.current = true
        scrollToBottom()
        setTimeout(() => {
          isScrollingRef.current = false
        }, 50)
      })
    }
  }, [...dependencies])

  // Initial scroll on mount
  useEffect(() => {
    if (scrollOnMount && containerRef.current) {
      const timeout = window.setTimeout(() => {
        isScrollingRef.current = true
        scrollToBottom()
        setTimeout(() => {
          isScrollingRef.current = false
        }, 50)
      }, 100)

      return () => {
        clearTimeout(timeout)
      }
    }
  }, [scrollOnMount, scrollToBottom])

  return {
    containerRef,
    sentinelRef,
    isNearBottom,
    scrollToBottom,
    checkIsNearBottom
  }
}
